import Style                           from './Table.style';
import {
  FC, ReactNode, useCallback,
  useEffect, useMemo, useRef, useState
}                                      from 'react';
import { useSubject }                  from 'View/HookApi';
import { delay }                       from 'rxjs';
import { useDispatch, useSelector }    from 'react-redux';
import { UnknownAction }               from 'redux';
import { IFilterSettings }             from 'Domain/Repositories/BaseRepository/BaseRepository';
import { InputRef, Table as AntTable } from 'antd';
import { TRootState }                  from 'Redux/Reducers';
import {
  argumentsToQuery, IFilterProps,
  getListDefaultState, TableUtils,
  TColumnProps, TExtendColumnProps, parseQueryArguments
} from 'Utils';
import { AnyObject }                   from 'antd/es/_util/type';
import { TableProps }                  from 'antd/es/table/InternalTable';
import { useLocation, useNavigate }    from 'react-router-dom';

type TProps = {
  filter?: IFilterSettings['filter'],
  sorting?: IFilterSettings['sorting'],
  children?: ReactNode,
  fetchAction: (payload: IFilterSettings) => UnknownAction,
  dataQuery: (state: TRootState, filter?: null | IFilterProps<any>) => AnyObject[],
  countQuery: (state: TRootState) => number,
  isLoading: (state: TRootState) => boolean,
  columns: TColumnProps[],
  scroll?: TableProps['scroll'],
}

export const Table: FC<TProps> = ({ scroll, sorting, filter, children, columns, isLoading, dataQuery, countQuery, fetchAction }) => {

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector(isLoading);
  const total = useSelector(countQuery);
  const data = useSelector(dataQuery);
  const searchInput = useRef<InputRef>(null);

  const urlProps = getListDefaultState({ page: 1, take: 10 });

  const [ filterSettings, setFilterSettings ] = useState<IFilterSettings>({
    // @ts-ignore
    sorting: urlProps.sorting,
    filter: urlProps.filter,
    pagination: { page: urlProps.page, take: urlProps.take },
  });
  const { page, take } = filterSettings.pagination!;

  const onRequest$ = useSubject(() => {
    let _filter = [ ...(filterSettings.filter || []) ];
    if(filter) {
      _filter = [ ..._filter, ...filter ];
    }
    let _sorting: IFilterSettings['sorting'] = filterSettings?.sorting || undefined;
    if((!_sorting || !_sorting.field || !_sorting.order) && !!sorting) {
      _sorting = {
        field: _sorting?.field || sorting.field,
        order: _sorting?.order || sorting.order,
      };
    }

    dispatch(fetchAction({ ...filterSettings, sorting: _sorting, filter: _filter }));
  }, [filterSettings], delay(300));

  useEffect(() => {
    const queryArguments = parseQueryArguments(`?${window.location.href.split('?')[1]}`);

    const { sorting, filter = null } = filterSettings;
    const { page, take } = filterSettings.pagination!;

    let query = argumentsToQuery({ ...sorting, page });
    if(filter && !!filter.length) {
      filter.filter(({ value }) => !!value).forEach(filter => {
        query += `&filter_${filter.field}=${filter.value}`;
      })
    }
    const noneTableProps = {};
    Object.keys(queryArguments).map((key) => {
      if(['field', 'order', 'page', 'take'].includes(key) || key.startsWith('filter_')) {
        return;
      }
      // @ts-ignore
      noneTableProps[key] = queryArguments[key];
    });
    const noneTablePropQuery = argumentsToQuery(noneTableProps);
    navigate(`${location.pathname}?${noneTablePropQuery ? `${noneTablePropQuery}&` : ''}${query}`, { replace: true });
    onRequest$.next(undefined);
  }, [filterSettings]);

  const handleOnSearch = ({ searchText, searchColumn }: { searchText: string, searchColumn: string }) => {
    // setSearchText(searchText);
    // setSearchedColumn(searchColumn);
  };

  const getColumn = (column: TColumnProps): TExtendColumnProps => ({
    title: column.label,
    key: column.key,
    dataIndex: column.key,
    width: column.width,
    fixed: column.fixed || column.key === 'actions' ? 'right' : undefined,
    render: (value, record) => (
      <div data-th={column.label}>
        {(column.render || TableUtils.renderByType(column.type ?? 'String', column?.length))(value, record)}
      </div>
    ),
    ...TableUtils.sorter({ ...column, sortable: column.sortable ?? true }),
    ...TableUtils.searcher({
      column: { ...column, searchable: column.searchable ?? true },
      onSearch: handleOnSearch,
      searchInput,
    }),
  });



  const _dataSource = useMemo(() => data.map((item: AnyObject) => ({
    ...item,
    key: item.id,
    ...TableUtils.getRawData(item, columns),
  })), [ data ]);
  const _columns = useMemo(() => columns.map(getColumn), [ columns ]);

  // @ts-ignore
  const handleOnChange= useCallback<TableProps['onChange']>((pagination, filters, sorter, extra) => {
    let page = pagination.current || 1;
    if(take * page > total - (total % take) + take) {
      page = 1;
    }

    const requestFilter: IFilterSettings = {
      filter: TableUtils.convertToRequestFilter({ filters, columns: _columns }),
      pagination: { page, take },
    }

    const sorting = TableUtils.convertToRequestSorting(sorter);
    if(sorting) {
      requestFilter.sorting = sorting;
    }

    setFilterSettings(requestFilter);
  }, [_columns, _dataSource]);

  return (
    <Style>
      <AntTable
        columns={_columns as any}
        dataSource={_dataSource}
        onChange={handleOnChange}
        loading={loading}
        pagination={{ current: page, pageSize: take, total }}
        footer={() => <div style={{ textAlign: 'right'}}><i style={{ fontSize: 14 }}>{total} Einträge</i> </div>}
        scroll={ scroll ?? { x: 800 }}
      />
      {children}
    </Style>
  );
};
